import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleTags?: string[];
}

function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogTitle,
  ogDescription,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleTags,
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = 'https://elegancepro.github.io';
  const canonical = canonicalPath ? `${baseUrl}${canonicalPath}` : `${baseUrl}${location.pathname}`;

  const siteName = '掌中美母';
  const defaultTitle = '掌中美母 - 专属美丽养护体验 | 妈妈护肤方案与形象提升';
  const defaultDescription =
    '掌中美母为现代母亲打造专属美丽养护方案。从肌肤护理到整体形象提升，帮助每一位妈妈在繁忙生活中找回自信光彩。';
  const defaultKeywords = '掌中美母,美母护肤方案,妈妈美丽养护,母亲形象提升,掌中美丽秘籍,妈妈自信焕新,母亲护肤推荐';

  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || finalDescription;

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    ...location.pathname
      .split('/')
      .filter((x) => x)
      .map((name, index, arr) => {
        const path = `/${arr.slice(0, index + 1).join('/')}`;
        return { name: decodeURIComponent(name), path };
      }),
  ];

  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${baseUrl}${item.path}`,
    })),
  };

  const webPageLD = {
    '@context': 'https://schema.org',
    '@type': ogType === 'article' ? 'Article' : 'WebPage',
    name: finalTitle,
    description: finalDescription,
    url: canonical,
    ...(ogType === 'article' && {
      headline: finalTitle,
      datePublished: articlePublishedTime,
      dateModified: articleModifiedTime || articlePublishedTime,
      ...(articleTags && { keywords: articleTags.join(', ') }),
    }),
  };

  const seoData = JSON.stringify([breadcrumbLD, webPageLD]);

  return (
    <>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      {articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}
      {articleTags && articleTags.length > 0 && (
        <meta name="article:tag" content={articleTags.join(', ')} />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: seoData }}
      />
    </>
  );
}

export default SEOHead;